import React, { FC, ReactNode } from 'react';
import * as Types from '../types';
import { Button } from 'antd';
import * as translations from '../strings';
import { getLangKey } from '../../../../../../javascript/models/Language';
import { Loader, XCircle, X as Close, Info } from 'lucide-react';
import animationStyles from './animation.module.css';
import cx from 'classnames';
import styles from './index.module.css';
import UAParser from 'ua-parser-js';
import { getShortcutSections } from './shortcutSections';
import { ShortcutKeys } from './ShortcutKeys';

const parser = new UAParser(navigator.userAgent);

type Props = {
  overlay: Types.Overlay;
  setOverlay: (overlay: Types.Overlay) => void;
  closeModal: () => void;
  subscriptionUrl: string;
  extensionsUrl: string;
  uploadProgress?: number;
  container?: HTMLElement | null;
};

type Action = {
  text: string;
  url?: string;
  onClick?: () => void;
};

type Back = {
  text: string;
  onClick: () => void;
};

type Content = {
  icon?: ReactNode;
  header?: string;
  message?: string;
  content?: ReactNode;
  close?: ReactNode;
  progress?: number;
  action?: Action;
  back?: Back;
} | null;

const strings = translations[getLangKey()];

const Overlay: FC<Props> = ({
  overlay,
  setOverlay,
  closeModal,
  subscriptionUrl,
  extensionsUrl,
  uploadProgress,
  container
}) => {
  const os = parser.getOS().name + ' ' + parser.getOS().version;
  const isMac: boolean = os.toLowerCase().includes('mac');
  const shortcutSections = getShortcutSections({ isMac });

  const content = (): Content => {
    switch (overlay) {
      case 'shortcuts':
        return {
          header: strings.keyboardShortcuts,
          content: (
            <div className={styles.content}>
              {shortcutSections.map((section, sectionIndex) => (
                <div className={styles.shortcutSection} key={`shortcut-section-${sectionIndex}`}>
                  {section.title && (
                    <h4 className={styles.shortcutSectionHeading}>{section.title}</h4>
                  )}
                  <div className={cx(styles.shortcutRows, styles.shortcutRowsGrid)}>
                    {section.rows.map((row, rowIndex) => (
                      <div
                        className={cx(styles.wrapper, styles.markdownWrapper)}
                        key={`${sectionIndex}-${rowIndex}-${row.label}`}
                      >
                        {row.pieces ? (
                          <ShortcutKeys
                            pieces={row.pieces}
                            isMac={isMac}
                            container={container}
                          />
                        ) : (
                          <div className={styles.codeContainer}>
                            <code>{row.shortcut}</code>
                          </div>
                        )}
                        <span>{row.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ),
          close: (
            <div
              className={styles.closeContainer}
              onClick={() => setOverlay(null)}
            >
              <Close className={styles.closeIcon} />
            </div>
          )
        };
      case 'loading':
        return {
          icon: (
            <Loader
              className={cx(styles.loaderIcon, animationStyles.loaderIcon)}
            />
          ),
          message: strings.creatingTask
        };
      case 'uploading':
        return {
          icon: (
            <Loader
              className={cx(styles.loaderIcon, animationStyles.loaderIcon)}
            />
          ),
          message: strings.videoUploading,
          progress: uploadProgress
        };
      case 'error':
        return {
          icon: <XCircle className={styles.errorIcon} />,
          message: strings.couldntCreate,
          action: {
            text: strings.tryAgain,
            onClick: () => setOverlay(null)
          }
        };
      case 'accountInUse':
        return {
          icon: <Info className={styles.infoIcon} />,
          message: strings.accountInUse,
          action: {
            text: strings.close,
            onClick: () => {
              closeModal();
              setOverlay(null);
              closeModal();
            }
          }
        };
      case 'extensionlessUpgrade':
        return {
          header: strings.premiumFeature,
          message: strings.extensionlessUnavailable,
          action: {
            text: strings.upgrade,
            url: subscriptionUrl
          },
          back: {
            text: strings.close,
            onClick: () => setOverlay(null)
          }
        };
      case 'screenshotGenerating':
        return {
          header: strings.screenshotPending,
          message: strings.pendingMessage,
          action: {
            text: strings.bugherdExtensions,
            url: extensionsUrl
          },
          back: {
            text: strings.close,
            onClick: () => setOverlay(null)
          }
        };
      default:
        return null;
    }
  };

  const overlayContent = content();

  if (!overlayContent) {
    return null;
  } else {
    const {
      icon,
      header,
      message,
      action,
      back,
      progress,
      content,
      close
    } = overlayContent;

    return (
      <div
        className={cx(styles.overlayOuter, {
          [styles.shortcuts]: overlay === 'shortcuts'
        })}
        data-cy="create-task-overlay"
      >
        {icon}
        {header && <h3 className={styles.moreInfoHeader}>{header}</h3>}
        <div className={styles.messageOuter}>
          {message && <p className={styles.moreInfoMessage}>{message}</p>}
          {typeof progress === 'number' && progress >= 0 && (
            <div className={styles.progressBar}>
              <div
                style={{ width: `${progress}%` }}
                className={cx(styles.progressInner, {
                  [styles.almostComplete]: progress >= 80
                })}
              />
            </div>
          )}
          {content}
        </div>
        <div className={styles.actionsContainer}>
          {action && (
            <Button
              type="primary"
              className={styles.action}
              onClick={() => action.onClick && action.onClick()}
              href={action && action.url}
            >
              {action.text}
            </Button>
          )}
          {back && (
            <Button className={styles.goBack} onClick={() => back.onClick()}>
              {back.text}
            </Button>
          )}
        </div>
        {close && close}
      </div>
    );
  }
};

export default Overlay;
